import cx from "classnames";
import { ContentState, EditorState, convertToRaw, Modifier, SelectionState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';

import { ToolbarNameButton } from './components/toolbar-name-button';
import { ToolbarLinkButton } from './components/toolbar-link-button';
import { ToolbarFieldChooserButton } from './components/toolbar-field-chooser-button/toolbar-field-chooser-button';
import { fieldRegistry } from './components/toolbar-field-chooser-button/toolbar-field-chooser-button';

import { ToolbarCustomButtonType } from "./text-editor.types";

import './text-editor.styles.scss';

// Custom HTML to Draft conversion that handles field spans
const customHtmlToDraft = (html) => {
  if (!html) return htmlToDraft("");
  
  // Convert field spans back to invisible markers and remove duplicate text
  let processedHtml = html.replace(
    /([^<]*?)(<span[^>]*class="[^"]*simple_field_placeholder[^"]*"[^>]*fieldid="([^"]*)"[^>]*systemfield="([^"]*)"[^>]*title="([^"]*)"[^>]*>([^<]*)<\/span>)/g,
    (match, textBefore, spanMatch, fieldId, systemField, title, spanText) => {
      // Store field data in registry for future use
      const fieldCounter = Date.now() + Math.random();
      const uniqueFieldId = `field_${Math.floor(fieldCounter)}_${Date.now()}`;
      
      fieldRegistry.set(uniqueFieldId, {
        fieldId,
        systemField,
        title,
        className: 'simple_field_placeholder',
        text: spanText
      });
      
      // Prepare the invisible marker that will be recognised later
      const invisibleMarker = `\u200B${uniqueFieldId}\u200B`;

      // If textBefore already ends with the field text we do not add it again
      if (textBefore.endsWith(spanText)) {
        return textBefore + invisibleMarker;
      }

      // Otherwise we insert the visible text followed by the marker so the user sees the field
      return textBefore + spanText + invisibleMarker;
    }
  );
  
  return htmlToDraft(processedHtml);
};

// Basic sanitizer to avoid DraftJS null-entity errors and ensure a valid first block
const sanitizeHtmlForDraft = (html) => {
  if (!html) return '';

  let safe = html;

  // Remove empty style attributes
  safe = safe.replace(/\sstyle="\s*"/gi, '');

  // Remove img style with empty width/height; keep the tag
  safe = safe.replace(/<img\b([^>]*?)style="[^"]*?(?:height:\s*;|width:\s*;)[^"]*"\s*([^>]*)>/gi, '<img $1$2>');

  // Wrap standalone <img ...> not inside a block with a paragraph
  safe = safe.replace(/(^|\n|>)(\s*)<img\b([^>]*)>(\s*)(?=($|\n|<))/gi, ($0, p1, ws1, attrs, ws2) => {
    return `${p1}${ws1}<p><img ${attrs}></p>`;
  });

  // Ensure the first non-whitespace node is a paragraph to keep a valid first block
  const startsWithBlock = /^(\s|<!--[^>]*-->)*(<p\b|<h[1-6]\b|<div\b|<ul\b|<ol\b|<table\b)/i.test(safe);
  if (!startsWithBlock) {
    safe = `<p></p>${safe}`;
  }

  return safe;
};

// Helper: convert HTML (with span placeholders) into an EditorState that
// attaches FIELD_PLACEHOLDER entities to the corresponding visible text.
const htmlToEditorState = (html) => {
  const safeHtml = sanitizeHtmlForDraft(html || '');
  const { contentBlocks, entityMap } = htmlToDraft(safeHtml);

  let contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
  let editorState = EditorState.createWithContent(contentState);

  // Parse placeholders from HTML so we can recreate entities
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = safeHtml || '';
  const spanElements = tempDiv.querySelectorAll('span.simple_field_placeholder');

  spanElements.forEach((spanEl) => {
    const spanText = spanEl.textContent || '';
    if (!spanText) return;

    const fieldId = spanEl.getAttribute('fieldid') || '';
    const systemField = spanEl.getAttribute('systemfield') || '';
    const title = spanEl.getAttribute('title') || '';
    const className = spanEl.getAttribute('class') || 'simple_field_placeholder';

    const content = editorState.getCurrentContent();
    content.getBlockMap().forEach((block) => {
      const text = block.getText();
      const start = text.indexOf(spanText);
      if (start !== -1) {
        const end = start + spanText.length;

        const selection = SelectionState.createEmpty(block.getKey()).merge({
          anchorOffset: start,
          focusOffset: end,
        });

        const contentWithEntity = content.createEntity('FIELD_PLACEHOLDER', 'IMMUTABLE', {
          fieldId,
          systemField,
          title,
          className,
        });
        const entityKey = contentWithEntity.getLastCreatedEntityKey();

        const newContent = Modifier.applyEntity(contentWithEntity, selection, entityKey);

        editorState = EditorState.push(editorState, newContent, 'apply-entity');
      }
    });
  });

  return editorState;
};

export const TextEditor = (props) => {
  const { onChange, defaultValue, value, onBlur, classes, className, toolbarCustomButtons, onFieldButtonClick } = props;
  const [editorState, setEditorState] = React.useState();

  React.useEffect(() => {
    if (defaultValue) {
      const state = htmlToEditorState(defaultValue);
      setEditorState(state);
    }
  }, []);

  React.useEffect(() => {
    if (defaultValue) {
      return
    }

    const newState = htmlToEditorState(value ?? "");

    if (!editorState) {
      setEditorState(newState);
    } else if (handleDraftToHtml(editorState) !== value) {
      setEditorState(newState);
    }

  }, [value]);

  const handleDraftToHtml = React.useCallback((state) => {
    const rawContent = convertToRaw(state.getCurrentContent());

    // Use draftjs-to-html entity transform to convert FIELD_PLACEHOLDER entities
    const html = draftToHtml(rawContent, undefined, undefined, (entity, originalText) => {
      if (entity.type === 'FIELD_PLACEHOLDER') {
        const { fieldId, systemField, title, className } = entity.data || {};
        return `<span class="${className || 'simple_field_placeholder'}" fieldid="${fieldId}" systemfield="${systemField}" title="${title}">${originalText}</span>`;
      }
      // Return undefined so that draftjs-to-html applies its default handling
      return undefined;
    });

    // Return HTML as-is to keep <p> wrapping around spans, matching DraftJS default
    return html;
  }, []);

  const handleChange = React.useCallback((state) => {
    setEditorState(state);

    if (onChange) {
      onChange(handleDraftToHtml(state));
    }
  }, [onChange, handleDraftToHtml]);

  const handleBlur = React.useCallback((data) => {
    if (onBlur) {
      onBlur(handleDraftToHtml(editorState));
    }
  }, [onBlur, editorState, handleDraftToHtml]);

  const predefinedButtons = React.useMemo(() => {
    return {
      [ToolbarCustomButtonType.name]: <ToolbarNameButton className="TextEditor__toolbar__button" editorState={editorState} onChange={handleChange} />,
      [ToolbarCustomButtonType.link]: <ToolbarLinkButton className="TextEditor__toolbar__button" editorState={editorState} onChange={handleChange} />,
      [ToolbarCustomButtonType.field]: onFieldButtonClick ? (
        <ToolbarFieldChooserButton 
          className="TextEditor__toolbar__button" 
          editorState={editorState} 
          onChange={handleChange}
          onClick={onFieldButtonClick}
        />
      ) : null,
    }
  }, [editorState, handleChange, onFieldButtonClick]);

  const defaultButtons = React.useMemo(() => {
    return [
      predefinedButtons[ToolbarCustomButtonType.name],
      predefinedButtons[ToolbarCustomButtonType.link],
    ]
  }, [predefinedButtons]);

  const processedToolbarCustomButtons = React.useMemo(() => {
    if (!toolbarCustomButtons) return defaultButtons;
    return toolbarCustomButtons
      .map((x) => {
        if (x.type && predefinedButtons[x.type]) {
          return predefinedButtons[x.type];
        }
        return x.component;
      })
      .filter((button) => button !== undefined && button !== null);
  }, [predefinedButtons, toolbarCustomButtons, defaultButtons]);

  if (!editorState) {
    return null;
  }

  return (
    <Editor
      onEditorStateChange={handleChange}
      editorState={editorState}
      wrapperClassName={cx("TextEditor", className)}
      toolbarClassName="TextEditor__toolbar"
      editorClassName={cx("TextEditor__work-area", classes?.workArea)}
      toolbarCustomButtons={processedToolbarCustomButtons || defaultButtons}
      onBlur={handleBlur}
      toolbar={{
        inline: {
          bold: { className: "TextEditor__toolbar__button" },
          italic: { className: "TextEditor__toolbar__button" },
          underline: { className: "TextEditor__toolbar__button" },
          strikethrough: { className: "TextEditor__toolbar__button" },
          monospace: { className: "TextEditor__toolbar__button" },
          superscript: { className: "TextEditor__toolbar__button" },
          subscript: { className: "TextEditor__toolbar__button" },
        },
        list: {
          unordered: { className: "TextEditor__toolbar__button" },
          ordered: { className: "TextEditor__toolbar__button" },
          indent: { className: "TextEditor__toolbar__button" },
          outdent: { className: "TextEditor__toolbar__button" },
        },
        textAlign: {
          left: { className: "TextEditor__toolbar__button" },
          center: { className: "TextEditor__toolbar__button" },
          right: { className: "TextEditor__toolbar__button" },
          justify: { className: "TextEditor__toolbar__button" },
        },
        colorPicker: { className: "TextEditor__toolbar__button" },
        link: {
          link: { className: "TextEditor__toolbar__button" },
          unlink: { className: "TextEditor__toolbar__button" },
        },
        embedded: { className: "TextEditor__toolbar__button" },
        emoji: { className: "TextEditor__toolbar__button" },
        image: { className: "TextEditor__toolbar__button" },
        remove: { className: "TextEditor__toolbar__button" },
        history: {
          undo: { className: "TextEditor__toolbar__button" },
          redo: { className: "TextEditor__toolbar__button" },
        },
      }}
    />
  );
};
